import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";

/**
 * /hotel-lobby-ai-video-generator 样片区的片子。
 *
 * 2026-10-01 先放 LightX 的 3 条外部样片（老公定的，同首页 Seedance 拼贴的做法），全是 16:9。
 * 同日试过 aihotellobby.net 的 6 条，已撤（R2 上的文件还在，没引用）。LightX 三条带音轨（AAC 96k），默认静音；
 * 来源和压制参数见 packages/scripts/hotel-lobby-showcase/README.md。外部片 onSite = false：
 * 文案不说「在本站生成」，也不进 VideoObject 结构化数据。
 *
 * 以后换成站内出片：同一套预设出片（规矩见 packages/scripts/i2v-samples/manifest.ts 文件头），
 * 输入图用 AI 出的原创人物 / 宠物，传到 R2 `home/showcase/hotel-lobby/` 的
 * clips/{id}.mp4、posters/{id}.jpg、refs/{id}-left.jpg / -right.jpg，onSite 设 true。
 *
 * 清单为空时样片区整个不渲染。
 */
export type HotelLobbyShowcaseKind = "pets" | "mixed";

export interface HotelLobbyShowcaseClip {
	id: string;
	title: string;
	/** 一句话说明，JSON-LD VideoObject.description 与 <video> aria-label 用 */
	description: string;
	/** 拼贴 hover 时显示的类别，文案在 hotelLobby.showcase.kinds */
	kind: HotelLobbyShowcaseKind;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	/**
	 * Studio 右栏 Examples 大预览用的 1280×720 版（2026-10-23）：预览 650–770px 宽，retina 上 720 宽的
	 * 小文件糊。拼贴 tile 只有 ~340px 宽，三条同时播，继续用 videoUrl 的小文件
	 */
	previewVideoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 左右两张原图（R2 refs/{id}-left.jpg / -right.jpg），「Try this example」填进 hero 的两个槽 */
	inputImages: [string, string];
}

const R2_BASE = "home/showcase/hotel-lobby";

/**
 * 视频地址的版本参数。assets 域的 GET 走 Cloudflare 边缘缓存（max-age 一天），同一个 key
 * 覆盖上传后一天内还是旧文件；换片子时把这个数字加一。2 = 2026-10-01 带音轨重压
 */
const CLIP_VERSION = 2;

type Spec = Omit<
	HotelLobbyShowcaseClip,
	"videoUrl" | "previewVideoUrl" | "poster" | "inputImages"
>;

/** 顺序即拼贴顺序：3 列、每列一条 */
const SPECS: Spec[] = [
	{
		id: "lightx-dog-cat",
		title: "Dog and Cat Duet",
		description:
			"A golden retriever and a tabby cat stand on their back legs and sing at one hanging mic.",
		kind: "pets",
		orientation: "landscape",
		onSite: false,
		duration: 8,
		hasAudio: true,
	},
	{
		id: "lightx-man-dog",
		title: "Owner and Dog Duo",
		description:
			"A man in a black suit and his golden retriever trade bars on the orange stage.",
		kind: "mixed",
		orientation: "landscape",
		onSite: false,
		duration: 8,
		hasAudio: true,
	},
	{
		id: "lightx-woman-cat",
		title: "Owner and Cat Duo",
		description:
			"A woman in a black tank top and a tabby cat share the mic on the orange stage.",
		kind: "mixed",
		orientation: "landscape",
		onSite: false,
		duration: 8,
		hasAudio: true,
	},
];

export const hotelLobbyShowcaseClips: HotelLobbyShowcaseClip[] = SPECS.map(
	(spec) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}.mp4?v=${CLIP_VERSION}`,
		),
		previewVideoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}-720p.mp4`,
		),
		// 封面也换 1280 宽的源图：大预览按 1080 宽取，720 宽的源图 _next/image 不放大
		poster: buildR2AssetsImageUrl(`${R2_BASE}/posters/${spec.id}-720p.jpg`),
		inputImages: [
			buildR2AssetsImageUrl(`${R2_BASE}/refs/${spec.id}-left.jpg`),
			buildR2AssetsImageUrl(`${R2_BASE}/refs/${spec.id}-right.jpg`),
		],
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const HOTEL_LOBBY_SHOWCASE_UPLOADED_AT = "2026-10-01T00:00:00+08:00";
